import { useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, CreditCard, ArrowRight } from "lucide-react";
import { buyBundle } from "@/lib/buy";

const CHECKLIST = [
  "Attaches to standard garden hoses — no special equipment",
  "24–48 hour protective barrier against radiant heat and embers",
  "Biodegradable, non-toxic — safe for soil, pets and wildlife",
  "Adheres to wood, glass, metal, stone and foliage",
  "Rinses off with clean water after the event — no staining",
];

export default function ProductSection() {
  const [buying, setBuying] = useState(false);
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);

  return (
    <section id="buy" className="py-20 lg:py-28 bg-[#faf9f7]" data-testid="product-section">
      <div className="max-w-6xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
        >
          <div className="font-tech text-[11px] tracking-[0.3em] text-[#f97316] uppercase mb-4" data-testid="product-kicker">
            The product
          </div>
          <h2 className="font-display font-bold tracking-tight text-3xl sm:text-4xl text-[#17150f] mb-5" data-testid="product-headline">
            NOXWAY Formula 100 — 5kg bag
          </h2>
          <p className="text-base text-stone-600 leading-relaxed mb-8" data-testid="product-description">
            When wildfire is coming, preparation matters. Apply NOXWAY to appropriate areas before exposure,
            following the product label and application instructions.
          </p>
          <ul className="space-y-4" data-testid="product-checklist">
            {CHECKLIST.map((item, i) => (
              <li key={item} className="flex items-start gap-3" data-testid={`checklist-item-${i}`}>
                <CheckCircle2 size={20} className="text-[#f97316] shrink-0 mt-0.5" strokeWidth={2.2} />
                <span className="text-sm sm:text-base text-stone-700 leading-relaxed">{item}</span>
              </li>
            ))}
          </ul>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="lg:sticky lg:top-24"
        >
          <div className="rounded-2xl border border-stone-200 bg-white p-8 shadow-sm" data-testid="info-request-card">
            <div className="font-tech text-[10px] tracking-[0.25em] text-[#f97316] uppercase font-bold" data-testid="info-card-badge">
              Order now — ships with hose kit + guide
            </div>
            <h3 className="font-display font-bold text-2xl text-[#17150f] mt-4 mb-2" data-testid="info-card-title">
              Order Formula 100
            </h3>

            <div className="mb-6 flex items-end gap-3" data-testid="product-price-row">
              <div className="font-display font-bold text-4xl text-[#17150f]" data-testid="product-price">CA$1,299</div>
              <div className="text-xs text-stone-500 pb-1.5">per 5kg bag</div>
            </div>

            <button
              onClick={() => {
                setBuying(true);
                buyBundle("noxway_formula_100", { customer_email: email || undefined, recovery_consent: consent && !!email });
                setTimeout(() => setBuying(false), 1500);
              }}
              disabled={buying}
              data-testid="product-buy-button"
              className="w-full inline-flex items-center justify-center gap-2.5 font-display font-bold text-sm px-8 py-4 rounded-md bg-[#f97316] text-white hover:bg-[#ea580c] transition-colors disabled:opacity-70"
            >
              {buying ? <Loader2 size={16} className="animate-spin" /> : <CreditCard size={16} />}
              {buying ? "REDIRECTING…" : "BUY NOW — CA$1,299"}
            </button>

            <div className="mt-5 space-y-3">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email (optional — for order follow-up)"
                data-testid="checkout-email-input"
                className="w-full bg-white border border-stone-200 rounded-md px-4 py-3 text-sm outline-none focus:border-[#f97316] placeholder:text-stone-400 text-[#17150f]"
              />
              <label className="flex items-start gap-3 text-xs text-stone-500 cursor-pointer">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  data-testid="recovery-consent-checkbox"
                  className="accent-[#f97316] w-4 h-4 mt-0.5 shrink-0"
                />
                Email me a one-time reminder if I don't complete my order.
              </label>
            </div>

            <a
              href="mailto:sales@noxway.shop?subject=NOXWAY%20Information%20Request&body=Hi%20NOXWAY%20team%2C%0A%0AI%27d%20like%20product%20information%2C%20availability%20and%20pricing%20for%20NOXWAY%E2%84%A2.%0A%0AProperty%20type%3A%0ALocation%3A%0AApproximate%20area%20to%20protect%3A%0A%0AThank%20you."
              data-testid="product-request-button"
              className="mt-5 w-full inline-flex items-center justify-center gap-2 font-display font-bold text-sm px-8 py-3.5 rounded-md border border-stone-300 text-[#17150f] hover:border-[#f97316] hover:text-[#f97316] transition-colors"
            >
              REQUEST BULK INFO
              <ArrowRight size={15} />
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
